/**
 * "Not read yet" (Sofia, Oct 23 2026): fits from the list whose full profile has not been read and
 * checked — a run that was stopped, or a profile that could not be read. They are kept OFF the
 * shortlist: on Oct 22 every rejection on a Fleet shortlist was of someone whose profile had never
 * been read, and the full read is what turned 18 of 50 fits into misfits. Here they can be read one
 * by one or all at once (the same reading loop as a sourcing run), or rejected from the card.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import type { OutreachCandidate } from '../../types/taOutreach';
import { RunStatus } from './RunStatus';
import { ShortlistCard } from './ShortlistCard';
import { READ_ID, type useSourcingRun } from './useSourcingRun';

export function UnreadList({
  people,
  run,
  canRun,
  busy,
  onDecide,
}: {
  people: OutreachCandidate[];
  run: ReturnType<typeof useSourcingRun>;
  /** The FINN Outreach Sender is installed in a version that can read profiles. */
  canRun: boolean;
  busy: string | null;
  onDecide: (c: OutreachCandidate, d: 'accept' | 'reject', reason?: string) => void;
}) {
  const reading = run.runningId === READ_ID;
  const status = run.status?.jobId === READ_ID ? run.status : null;
  const free = canRun && !run.runningId;
  // Only people with a Recruiter id can be opened (CodeRabbit, PR #713).
  const readable = people.filter((c) => c.recruiterId);
  return (
    <details open={reading || undefined} className="space-y-3">
      <summary className="cursor-pointer font-semibold">Not read yet ({people.length})</summary>
      <p className="m-0 mt-1 text-[#707070]">
        The list card fits, but the full profile has not been read and checked. They join the shortlist once it is.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {reading ? (
          <Button variant="outline" size="sm" className="rounded-md" onClick={run.stop}>
            Stop
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="rounded-md"
            disabled={!free || !readable.length}
            title={
              !canRun
                ? 'Install or update the FINN Outreach Sender — see the yellow box at the top of this step'
                : !readable.length
                  ? 'No Recruiter link for these people, so their profiles cannot be opened. Open them in Recruiter yourself.'
                  : undefined
            }
            onClick={() => void run.readPeople(readable)}
          >
            Read all {readable.length}
          </Button>
        )}
        {status && <RunStatus key={status.since} status={status} running={reading} />}
      </div>
      <div className="space-y-3">
        {people.map((c) => (
          <ShortlistCard
            key={c.id}
            c={c}
            unread
            busy={busy === c.id || reading}
            onRead={free && c.recruiterId ? () => void run.readPeople([c]) : undefined}
            onDecide={(d, r) => onDecide(c, d, r)}
          />
        ))}
      </div>
    </details>
  );
}
